import {
  Button,
  Card,
  CardActions,
  CardContent,
  colors,
  Grid,
  Modal,
  Switch,
  Theme,
  Typography
} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import clsx from 'clsx';
import React, {useEffect, useState} from 'react';
import {T} from 'react-polyglot-hooks';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {useSnackbar} from 'notistack';
import getGroup from './getGroup.api';
import anyLoading from '../../../utils/anyLoading';
import mergeRoom, {MergeRoomInput} from './mergeRoom.api';
import {checkRoomAvailable} from '../../Resources/AvailableHotelModal/AvailableHotelModal';
import availableHotels from '../../Resources/AvailableHotelModal/getAvailableHotels.api';
import {RoomParty} from '../../../graphql/groups.datatypes';
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 700,
    maxHeight: '90%',
    overflowY: 'auto',
    maxWidth: '95%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  deleteButton: {
    color: theme.palette.white,
    backgroundColor: colors.red[600],
    '&:hover': {
      backgroundColor: colors.red[900]
    }
  },
  submitButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  }
}));

const MergeRoomPartyModal = (props: {
  className?: string,
  onClose: () => void,
  onUpdate?: () => void,
  open: boolean,
  groupId: number,
}) => {
  const {open, onClose, onUpdate, groupId, ...rest} = props;
  const classes = useStyles();
  const [formState, setFormState] = useState({primaryId: -1, secondaryId: -1});
  const groupData = getGroup(groupId);
  const hotelsData = availableHotels(groupData.data && groupData.data.group ? groupData.data.group.tour_resource_id : -1);
  const [sendMergeRoom, mergeRoomData] = mergeRoom();
  const {enqueueSnackbar} = useSnackbar();
  const loadingData = anyLoading(groupData, mergeRoomData, hotelsData);
  const group = groupData.data ? groupData.data.group : null;
  const [roomAvailable, setRoomAvailable] = useState(false);

  const getHotelById = (id: number) => hotelsData.data!.availableHotels.find(hotel => hotel.id === id);
  const getRoomById = (id: number) => group!.bookings!.reduce((target: RoomParty | undefined, booking) => target ? target : booking.roomParties!.find(roomParty => roomParty.id === id), undefined);

  useEffect(() => {
    let roomSet = false;

    if (hotelsData.data && hotelsData.data.availableHotels) {
      if (formState.primaryId !== -1 && formState.secondaryId !== -1) {
        const parentRoom = getRoomById(formState.primaryId);
        const childRoom = getRoomById(formState.secondaryId);

        if (parentRoom && childRoom) {
          const guestCapacity = parentRoom.members!.length + childRoom.members!.length;
          const startDate = moment(new Date(parentRoom.start_date!));
          const endDate = moment(new Date(parentRoom.end_date!));
          const selectedHotel = getHotelById(parentRoom.hotel!.id);

          //check if parent room has capacity in current room. Else check for larger room.
          let available = false;
          if (parentRoom.roomResource!.resource!.guest_capacity! >= guestCapacity || childRoom.roomResource!.resource!.guest_capacity! >= guestCapacity) {
              available = true;
            } else {

            if (selectedHotel) {
              available = checkRoomAvailable(selectedHotel, startDate, endDate, guestCapacity);
            }
          }
            roomSet = true;
            setRoomAvailable(available);
          }
      }
    }

    if (!roomSet) setRoomAvailable(false);
  }, [formState, groupData.data, hotelsData.data])

  const setPrimaryRoom = (id: number, checked: boolean) => {
    setFormState({
      ...formState,
      primaryId: checked ? id : -1,
      secondaryId: id === formState.secondaryId ? -1 : formState.secondaryId,
    });
  }
  const setSecondaryRoom = (id: number, checked: boolean) => {
    setFormState({
      ...formState,
      primaryId: id === formState.primaryId ? -1 : formState.primaryId,
      secondaryId: checked ? id : -1,
    });
  }

  const handleSave = () => {
    const sanitisedData: MergeRoomInput = {
      data: {
        parent_id: formState.primaryId,
        child_id: formState.secondaryId,
      }
    };

    sendMergeRoom({variables: sanitisedData})
        .then(() => {
          enqueueSnackbar('Room Party Saved.', {variant: 'success'});
          onClose();
          if (onUpdate) onUpdate();
        })
        .catch(() => {
          enqueueSnackbar('Save Room Party Failed!', {variant: 'error'});
          onClose();
        });
  };

  if (!open) {
    return null;
  }

  return (
      <Modal onClose={onClose} open={open}>
        <Card {...rest} className={clsx(classes.root)}>
          <form>
            <LoadingOverlay loading={loadingData.loading}>
              <CardContent>
                <Typography gutterBottom variant="h3">Merge Room Parties</Typography>

                <Grid container spacing={3}>
                  <Grid item xs={6}><Typography variant={'body1'}>Primary Member</Typography></Grid>
                  <Grid item xs={3}><Typography variant={'body1'}>Hotel</Typography></Grid>
                  <Grid item xs={1}><Typography variant={'body1'}>Guests</Typography></Grid>
                  <Grid item xs={1}><Typography variant={'body1'}>Parent</Typography></Grid>
                  <Grid item xs={1}><Typography variant={'body1'}>Child</Typography></Grid>
                  {group && group.bookings && group.bookings.map((booking) => {
                    if (booking.roomParties) return booking.roomParties.map(roomParty => {
                      const primary = roomParty.members!.find(member => member.primary_occupant);

                      return (
                          <React.Fragment key={roomParty.id}>
                            <Grid item xs={6}>
                              <Typography variant={'body1'}>
                                {primary && (primary.groupMember!.first_name! + ' ' + primary.groupMember!.last_name!)}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography variant={'body1'}>
                                {roomParty.hotel!.internal_name}
                              </Typography>
                            </Grid>
                            <Grid item xs={1}>
                              <Typography variant={'body1'}>
                                {roomParty.members!.length}
                              </Typography>
                            </Grid>
                            <Grid item xs={1}>
                              <Switch
                                  checked={formState.primaryId === roomParty.id}
                                  color="secondary"
                                  edge="start"
                                  onChange={(event, checked) => setPrimaryRoom(roomParty.id, checked)}
                              />
                            </Grid>
                            <Grid item xs={1}>
                              <Switch
                                  checked={formState.secondaryId === roomParty.id}
                                  color="secondary"
                                  edge="start"
                                  onChange={(event, checked) => setSecondaryRoom(roomParty.id, checked)}
                              />
                            </Grid>
                          </React.Fragment>
                      )
                    })
                  })}

                </Grid>

                <Typography gutterBottom variant="body1">
                  Can Merge? {roomAvailable ? 'yes' : 'no'}
                </Typography>

              </CardContent>
              <CardActions className={classes.actions}>
                <Button onClick={() => {
                  onClose();
                }} variant="contained">
                  <T phrase="Cancel"/>
                </Button>
                <Button className={classes.submitButton} onClick={handleSave} variant="contained"
                        disabled={formState.primaryId === -1 || formState.secondaryId === -1 || !roomAvailable}>
                  <T phrase="Confirm"/>
                </Button>
              </CardActions>
            </LoadingOverlay>
          </form>
        </Card>
      </Modal>
  );
};

export default MergeRoomPartyModal;
